import { 
  doc, 
  getDoc, 
  setDoc, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { OrganizationSettings } from '../types';

// Default industries for organizations
const DEFAULT_INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Manufacturing',
  'Retail & E-Commerce',
  'Education',
  'Real Estate',
  'Professional Services',
  'Media & Entertainment',
  'Transportation & Logistics',
  'Energy & Utilities',
  'Agriculture',
  'Non-Profit',
  'Government',
  'Telecommunications',
  'Hospitality',
  'Construction',
  'Pharmaceutical',
  'Automotive',
  'Other'
];

export const settingsService = {
  // Get organization settings
  async getOrganizationSettings(orgId: string): Promise<OrganizationSettings> {
    try {
      const docRef = doc(db, 'settings', orgId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          orgId: data.orgId,
          industries: data.industries || DEFAULT_INDUSTRIES,
          contactStages: data.contactStages,
          opportunityStages: data.opportunityStages,
          defaultProducts: data.defaultProducts,
          defaultLogoUrl: data.defaultLogoUrl,
          updatedAt: data.updatedAt?.toDate() || new Date(),
          updatedBy: data.updatedBy
        };
      }
      
      // Create default settings if none exist
      const defaultSettings: OrganizationSettings = {
        id: orgId,
        orgId,
        industries: DEFAULT_INDUSTRIES,
        updatedAt: new Date(),
        updatedBy: 'system'
      };
      
      await this.updateOrganizationSettings(orgId, defaultSettings, 'system');
      return defaultSettings;
    } catch (error) {
      console.error('Error getting organization settings:', error);
      // Return defaults on error
      return {
        id: orgId,
        orgId,
        industries: DEFAULT_INDUSTRIES,
        updatedAt: new Date(),
        updatedBy: 'system'
      };
    }
  },

  // Update organization settings (admin only)
  async updateOrganizationSettings(
    orgId: string,
    updates: Partial<OrganizationSettings>,
    updatedBy: string
  ): Promise<void> {
    try {
      const docRef = doc(db, 'settings', orgId);
      
      await setDoc(docRef, {
        ...updates,
        orgId,
        updatedAt: Timestamp.now(),
        updatedBy
      }, { merge: true });
    } catch (error) {
      console.error('Error updating organization settings:', error);
      throw error;
    }
  },

  // Add a new industry to the list
  async addIndustry(orgId: string, industry: string, updatedBy: string): Promise<void> {
    const settings = await this.getOrganizationSettings(orgId);
    if (!settings.industries.includes(industry)) {
      settings.industries.push(industry);
      settings.industries.sort();
      await this.updateOrganizationSettings(orgId, { industries: settings.industries }, updatedBy);
    }
  },

  // Remove an industry from the list
  async removeIndustry(orgId: string, industry: string, updatedBy: string): Promise<void> {
    const settings = await this.getOrganizationSettings(orgId);
    const filtered = settings.industries.filter(i => i !== industry);
    await this.updateOrganizationSettings(orgId, { industries: filtered }, updatedBy);
  },

  // Get default industries list
  getDefaultIndustries(): string[] {
    return [...DEFAULT_INDUSTRIES];
  },

  // Add a user-defined lead source to the org list
  async addLeadSource(orgId: string, source: string, updatedBy: string): Promise<void> {
    const value = source.trim();
    if (!value) return;
    const settings = await this.getOrganizationSettings(orgId);
    const current = settings.customLeadSources || [];
    if (!current.includes(value)) {
      const next = [...current, value].sort();
      await this.updateOrganizationSettings(orgId, { customLeadSources: next }, updatedBy);
    }
  },

  // Remove a user-defined lead source from the org list
  async removeLeadSource(orgId: string, source: string, updatedBy: string): Promise<void> {
    const settings = await this.getOrganizationSettings(orgId);
    const filtered = (settings.customLeadSources || []).filter(s => s !== source);
    await this.updateOrganizationSettings(orgId, { customLeadSources: filtered }, updatedBy);
  }
};
